import axios from 'axios';

// Get base API URL - VITE_AUTH_SERVICE_URL should be just the base (e.g., https://api.mingyin.hk/api/v1/auth)
// or fall back to VITE_API_BASE_URL + /api/v1/auth
const getAuthBaseUrl = () => {
  const authUrl = import.meta.env.VITE_AUTH_SERVICE_URL;
  if (authUrl) {
    // If it already ends with /api/v1/auth, use it directly for kiosk endpoints
    if (authUrl.endsWith('/api/v1/auth')) {
      return authUrl.replace('/api/v1/auth', '');
    }
    return authUrl;
  }
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3101';
};

const API_BASE_URL = getAuthBaseUrl();

export interface KioskSession {
  sessionId: string;
  staffId: string;
  staffName: string;
  staffEmail: string;
  role: string;
  createdAt: number;
  expiresAt: number;
}

interface ValidateSessionResponse {
  success: boolean;
  valid: boolean;
  session?: KioskSession;
}

interface RefreshSessionResponse {
  success: boolean;
  sessionToken: string;
  expiresAt: number;
}

/**
 * Why a session call failed matters to the person standing at the kiosk:
 * - 'invalid'     -> the token really was rejected; a NEW enrollment session is needed.
 * - 'unreachable' -> we never got an answer (proxy 502, backend down, no network).
 *                    The token is probably fine and retrying can succeed.
 * Collapsing the two is what made an API-proxy outage look like an expired session.
 */
export type SessionFailureStatus = 'invalid' | 'unreachable';

export type ValidateSessionOutcome =
  | { status: 'valid'; session: KioskSession }
  | { status: SessionFailureStatus };

export type RefreshSessionOutcome =
  | { status: 'valid'; sessionToken: string; expiresAt: number }
  | { status: SessionFailureStatus };

/**
 * Only the backend's own documented answer may declare a token bad. Its contract:
 *  - validate-session ALWAYS answers HTTP 200; a rejected token is `{success:true, valid:false}`
 *    (the controller catches every validation error). So an error status of ANY kind - including a
 *    401/403 injected by a gateway, WAF or misconfigured guard - is not a verdict on the token.
 *  - refresh-session lets UnauthorizedException through, so ONLY HTTP 401 rejects the token.
 *  - Any 2xx whose body is not the documented shape (an nginx error page, the SPA index.html served
 *    by a broken proxy route) came from something that is not the auth service.
 * Everything outside that contract is 'unreachable': it must never clear a live enrollment session.
 */
const REFRESH_TOKEN_REJECTED_STATUS = 401;

function responseStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null | undefined)?.response?.status;
}

class KioskAuthService {
  /**
   * Validate a kiosk session token.
   */
  async validateSession(sessionToken: string): Promise<ValidateSessionOutcome> {
    try {
      const response = await axios.post<ValidateSessionResponse>(
        `${API_BASE_URL}/api/v1/auth/kiosk/validate-session`,
        { sessionToken }
      );
      const data = response.data;

      if (data?.success === true && data.valid === true && data.session) {
        return { status: 'valid', session: data.session };
      }

      // The one and only token verdict this endpoint gives.
      if (data?.success === true && data.valid === false) {
        return { status: 'invalid' };
      }

      console.error('Unrecognised validate-session payload; treating as unreachable:', data);
      return { status: 'unreachable' };
    } catch (error) {
      console.error('Failed to validate kiosk session (unreachable):', error);
      return { status: 'unreachable' };
    }
  }

  /**
   * Refresh a kiosk session, extending its expiry.
   */
  async refreshSession(sessionToken: string): Promise<RefreshSessionOutcome> {
    try {
      const response = await axios.post<RefreshSessionResponse>(
        `${API_BASE_URL}/api/v1/auth/kiosk/refresh-session`,
        { sessionToken }
      );
      const data = response.data;

      if (
        data?.success === true &&
        typeof data.sessionToken === 'string' &&
        typeof data.expiresAt === 'number'
      ) {
        return {
          status: 'valid',
          sessionToken: data.sessionToken,
          expiresAt: data.expiresAt,
        };
      }

      console.error('Unrecognised refresh-session payload; treating as unreachable:', data);
      return { status: 'unreachable' };
    } catch (error) {
      const status = responseStatus(error);

      if (status === REFRESH_TOKEN_REJECTED_STATUS) {
        // A 401 tells us the SHAPE of a rejection, not that the auth service produced it -
        // a gateway, WAF or proxy can inject one just as easily as it can on validate-session.
        // Acting on it alone would discard a live session and a half-filled health
        // questionnaire, so confirm with the one endpoint that gives an unambiguous verdict.
        console.warn('Kiosk session refresh returned 401; confirming with validate-session');
        const confirmation = await this.validateSession(sessionToken);

        if (confirmation.status === 'invalid') {
          return { status: 'invalid' };
        }

        // Either the token is still good, or we could not get a trustworthy answer.
        // Keep the session alive and let the caller retry while time remains.
        return { status: 'unreachable' };
      }

      console.error(`Failed to refresh kiosk session (unreachable, status ${status}):`, error);
      return { status: 'unreachable' };
    }
  }
}

export const kioskAuthService = new KioskAuthService();
